import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clsx } from 'clsx';

import { call, callStream } from '@spacefill/shared/src/utils/Backend';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { safeLocalStorage } from '@spacefill/uikit/src/utils/storage';

import { AiChatThread } from './components/AiChatThread';
import { AiConversationList } from './components/AiConversationList';
import { AiDrawer } from './components/AiDrawer';
import { AiInputBar } from './components/AiInputBar';
import { AiPresetQuestions } from './components/AiPresetQuestions';
import { AiTopBar } from './components/AiTopBar';
import { AiWelcomeScreen } from './components/AiWelcomeScreen';
import { useAiChatHistory } from './hooks/useAiChatHistory';

const AI_CHAT_FULLSCREEN_STORAGE_KEY = 'analytics-chat-fullscreen';

export function AnalyticsChatPanel({ isOpen, onClose }) {
    const { t } = useTranslation();
    const { user } = useUserContext();

    const companyName =
        user?.customer?.company || user?.logisticProvider?.company || user?.carrier?.company || 'Spacefill';
    const firstName = user?.firstName || '';
    const userInitials =
        [user?.firstName, user?.lastName]
            .filter(Boolean)
            .map((part) => part.trim().charAt(0))
            .join('')
            .slice(0, 2) || 'U';

    const [isMounted, setIsMounted] = useState(isOpen);
    const [isVisible, setIsVisible] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(() => {
        return safeLocalStorage.getItem(AI_CHAT_FULLSCREEN_STORAGE_KEY) === 'true';
    });
    const [openSequenceKey, setOpenSequenceKey] = useState(0);
    const [activeConversationId, setActiveConversationId] = useState(() => crypto.randomUUID());
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStatus, setLoadingStatus] = useState(null);
    const [ratings, setRatings] = useState({});
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const readerRef = useRef(null);
    const hasNewReplyRef = useRef(false);

    const { conversations, saveConversation, deleteConversation } = useAiChatHistory();

    const rateMessage = async (msgIndex, rating) => {
        const msg = messages[msgIndex];
        const question = messages.slice(0, msgIndex).findLast((m) => m.role === 'user')?.content ?? '';
        setRatings((prev) => ({ ...prev, [msgIndex]: rating }));
        await call('analytics/chat/rate', { question, answer: msg.content, rating }, 'POST');
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) {
            setIsMounted(true);
            setIsVisible(false);
            setOpenSequenceKey((prev) => prev + 1);
            const timeout = window.setTimeout(() => setIsVisible(true), 16);
            return () => window.clearTimeout(timeout);
        }
        setIsVisible(false);
        readerRef.current?.cancel();
        readerRef.current = null;
        const timeout = setTimeout(() => setIsMounted(false), 320);
        return () => clearTimeout(timeout);
    }, [isOpen]);

    useEffect(() => {
        safeLocalStorage.setItem(AI_CHAT_FULLSCREEN_STORAGE_KEY, String(isFullscreen));
    }, [isFullscreen]);

    useEffect(() => {
        if (messages.length === 0 || !hasNewReplyRef.current) return;
        saveConversation(activeConversationId, messages, ratings);
    }, [messages, ratings, activeConversationId, saveConversation]);

    const sendMessage = async (messageText, confirmedSql) => {
        const text = messageText ?? input.trim();
        if ((!text && !confirmedSql) || isLoading) return;

        if (text) {
            setMessages((prev) => [...prev, { role: 'user', content: text }]);
            setInput('');
            setTimeout(scrollToBottom, 50);
        }
        setIsLoading(true);
        setLoadingStatus('thinking');

        try {
            const response = await callStream('analytics/chat/message', {
                message: text,
                confirmedSql,
                history: messages.map(({ role, content }) => ({ role, content })),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const reader = response.body.getReader();
            readerRef.current = reader;
            const decoder = new TextDecoder();
            let buffer = '';
            let gotResult = false;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                for (const line of lines) {
                    if (!line.startsWith('data: ')) continue;
                    const event = JSON.parse(line.slice(6));

                    if (event.type === 'status') {
                        setLoadingStatus(event.label);
                    } else if (event.type === 'result') {
                        gotResult = true;
                        hasNewReplyRef.current = true;
                        setMessages((prev) => [
                            ...prev,
                            {
                                role: 'assistant',
                                content: event.content,
                                requiresConfirmation: event.requiresConfirmation,
                                sql: event.sql,
                            },
                        ]);
                    }
                }
            }

            if (!gotResult) {
                setMessages((prev) => [
                    ...prev,
                    { role: 'assistant', content: t('An error occurred. Please try again.') },
                ]);
            }
        } catch {
            setMessages((prev) => [...prev, { role: 'assistant', content: t('An error occurred. Please try again.') }]);
        } finally {
            readerRef.current = null;
            setIsLoading(false);
            setLoadingStatus(null);
            setTimeout(scrollToBottom, 100);
            inputRef.current?.focus();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            sendMessage();
        }
    };

    const handleNewChat = () => {
        readerRef.current?.cancel();
        readerRef.current = null;
        hasNewReplyRef.current = false;
        setMessages([]);
        setInput('');
        setIsLoading(false);
        setLoadingStatus(null);
        setRatings({});
        setOpenSequenceKey((prev) => prev + 1);
        setActiveConversationId(crypto.randomUUID());
    };

    const handleSelectConversation = (conv) => {
        readerRef.current?.cancel();
        readerRef.current = null;
        hasNewReplyRef.current = false;
        setMessages(conv.messages);
        setInput('');
        setIsLoading(false);
        setLoadingStatus(null);
        setRatings(conv.ratings ?? {});
        setActiveConversationId(conv.id);
        setOpenSequenceKey((prev) => prev + 1);
    };

    const handleDeleteConversation = (id) => {
        deleteConversation(id);
        if (id === activeConversationId) {
            handleNewChat();
        }
    };

    const isEmpty = messages.length === 0;

    const sidebar = (
        <AiConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={handleSelectConversation}
            onNew={handleNewChat}
            onDelete={handleDeleteConversation}
            t={t}
        />
    );

    const topBar = (
        <AiTopBar
            t={t}
            companyName={companyName}
            isFullscreen={isFullscreen}
            onNewChat={handleNewChat}
            onToggleFullscreen={() => setIsFullscreen((prev) => !prev)}
            onClose={onClose}
        />
    );

    return (
        <>
            <AiDrawer
                isMounted={isMounted}
                isVisible={isVisible}
                isFullscreen={isFullscreen}
                onBackdropClick={onClose}
                topBar={topBar}
                sidebar={sidebar}
            >
                <div
                    className={clsx(
                        'flex flex-1 flex-col overflow-y-auto bg-white transition-opacity duration-200',
                        isVisible ? 'opacity-100' : 'opacity-0'
                    )}
                >
                    {isEmpty ? (
                        <AiWelcomeScreen
                            t={t}
                            sequenceKey={openSequenceKey}
                            firstName={firstName}
                        />
                    ) : (
                        <AiChatThread
                            messages={messages}
                            isLoading={isLoading}
                            loadingStatus={loadingStatus}
                            ratings={ratings}
                            onRate={rateMessage}
                            messagesEndRef={messagesEndRef}
                            onConfirmSql={(sql) => sendMessage(undefined, sql)}
                            onReplay={sendMessage}
                            t={t}
                            userInitials={userInitials}
                        />
                    )}
                </div>
                {isEmpty ? (
                    <div className='shrink-0 bg-white px-4 pb-4 pt-2'>
                        <p className='mb-2 text-[11px] font-bold tracking-[0.08em] text-gray-400 uppercase'>
                            {t('Start with...')}
                        </p>
                        <AiPresetQuestions
                            onSelect={sendMessage}
                            isFullscreen={isFullscreen}
                        />
                    </div>
                ) : null}
                <AiInputBar
                    input={input}
                    isLoading={isLoading}
                    onChange={setInput}
                    onKeyDown={handleKeyDown}
                    onSend={() => sendMessage()}
                    inputRef={inputRef}
                    t={t}
                />
            </AiDrawer>
        </>
    );
}
